// Mobile-first "Message from the Owner" — replaces ZenGM's desktop Message view (src/ui/views/Message)
// for the annual owner performance evaluation shown after the draft. Same worker data (the message
// worker view: message.from / .year / .text HTML / .ownerMoods) and same underlying owner-mood logic
// from genMessage.ts; only the presentation changes to the Summer League dark card language (teal
// accent, Montserrat, GmNegotiationList/GmHome idioms).
//
// Two states:
//  - Normal evaluation: branded letter from the owner with a mood readout and the real evaluation
//    text, plus an owner-mood trend if there's enough history.
//  - Fired (gameOver): a dedicated "You've been fired" state whose ONLY action is "Start New League",
//    which routes to the home / league-creation screen ("/"). The fork ties you to one franchise, so
//    there is no "look for another job" / "run a new team" path here (see genMessage.ts + updatePlayMenu.ts,
//    where the multi-team job market is suppressed).

import useTitleBar from "../hooks/useTitleBar.tsx";
import { useLocal } from "../util/local.ts";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { SafeHtml } from "../components/SafeHtml.tsx";
import type { View } from "../../common/types.ts";

const moodMeta: Record<
	number,
	{ label: string; color: string; emoji: string }
> = {
	0: { label: "Horrible", color: "#E2504E", emoji: "🔥" },
	1: { label: "Bad", color: "#E8724E", emoji: "😠" },
	2: { label: "OK", color: "#E8B923", emoji: "😐" },
	3: { label: "Good", color: "#4A9EFF", emoji: "🙂" },
	4: { label: "Excellent", color: "#18E0A8", emoji: "🏆" },
};

const scoreFromTotal = (total: number) => {
	if (total >= 2) return 4;
	if (total >= 1) return 3;
	if (total >= 0) return 2;
	if (total > -0.5) return 1;
	return 0;
};

const GmMessage = ({ message }: View<"message">) => {
	const title = message?.subject ?? "Message";
	useTitleBar({ title });

	const { gameOver, teamInfoCache, userTid } = useLocal([
		"gameOver",
		"teamInfoCache",
		"userTid",
	]);
	const team = teamInfoCache[userTid];

	if (!message) {
		return (
			<div className="gmmsg">
				<style>{styles}</style>
				<div className="gmmsg-empty">Message not found.</div>
			</div>
		);
	}

	const startNewLeague = () => {
		realtimeUpdate([], "/");
	};

	const moods = message.ownerMoods ?? [];
	const latestMood = moods.at(-1);
	const total = latestMood
		? latestMood.money + latestMood.playoffs + latestMood.wins
		: 2;
	const mood = moodMeta[scoreFromTotal(total)]!;

	// Fired: a terminal state. The single path forward is a new league — no job hunt.
	if (gameOver) {
		return (
			<div className="gmmsg gmmsg-fired">
				<style>{styles}</style>

				<div className="gmmsg-firedbadge">🔥</div>
				<h1 className="gmmsg-firedtitle">You've been fired</h1>
				<div className="gmmsg-firedsub">
					From {message.from}
					{team ? ` · ${team.region} ${team.name}` : ""} · {message.year}
				</div>

				<div className="gmmsg-letter fired">
					<SafeHtml dirty={message.text} />
				</div>

				<p className="gmmsg-firednote">
					Your run with this franchise is over. Start fresh with a new team and
					build a dynasty from scratch.
				</p>

				<button className="gmmsg-cta" onClick={startNewLeague}>
					Start New League
				</button>
			</div>
		);
	}

	return (
		<div className="gmmsg">
			<style>{styles}</style>

			<header className="gmmsg-header">
				<div className="gmmsg-crest" style={{ background: "#22405a" }}>
					{team?.abbrev ?? "—"}
				</div>
				<div className="gmmsg-team">
					<div className="gmmsg-team-name">
						{team ? `${team.region} ${team.name}` : "Front Office"}
					</div>
					<div className="gmmsg-team-sub">
						{message.from} · {message.year}
					</div>
				</div>
			</header>

			<h1 className="gmmsg-title">Owner's Evaluation</h1>

			<div className="gmmsg-mood" style={{ borderColor: mood.color }}>
				<div className="gmmsg-mood-emoji">{mood.emoji}</div>
				<div className="gmmsg-mood-body">
					<div className="gmmsg-mood-lab">Owner mood</div>
					<div className="gmmsg-mood-val" style={{ color: mood.color }}>
						{mood.label}
					</div>
				</div>
			</div>

			<div className="gmmsg-letter">
				<div className="gmmsg-quote">“</div>
				<SafeHtml dirty={message.text} />
				<div className="gmmsg-sign">— {message.from}</div>
			</div>

			{moods.length > 2 ? (
				<div className="gmmsg-trend">
					<div className="gmmsg-trend-head">Owner mood by season</div>
					<div className="gmmsg-trend-rows">
						{moods.map((m) => {
							const t = m.money + m.playoffs + m.wins;
							const s = moodMeta[scoreFromTotal(t)]!;
							// Total spans roughly -1..3; map onto a 0..100% bar.
							const pct = Math.max(
								4,
								Math.min(100, ((t + 1) / 4) * 100),
							);
							return (
								<div className="gmmsg-trend-row" key={m.season}>
									<span className="gmmsg-trend-yr">{m.season}</span>
									<div className="gmmsg-trend-bar">
										<div
											className="gmmsg-trend-fill"
											style={{ width: `${pct}%`, background: s.color }}
										/>
									</div>
									<span
										className="gmmsg-trend-lab"
										style={{ color: s.color }}
									>
										{s.label}
									</span>
								</div>
							);
						})}
					</div>
					<div className="gmmsg-trend-note">
						The owner starts judging you two years after you're hired. If your
						overall mood sinks too low, you're fired.
					</div>
				</div>
			) : null}
		</div>
	);
};

const styles = `
.gmmsg { max-width:720px; margin:0 auto; margin-top:-1rem; padding:16px 16px 40px;
  font-family:Montserrat, system-ui, sans-serif; color:#fff;
  background:linear-gradient(#182a3a, #121212 220px); background-color:#121212; border-radius:12px; }
.gmmsg-header { display:flex; align-items:center; gap:12px; padding-top:6px; }
.gmmsg-crest { width:34px; height:34px; border-radius:50%; display:flex; align-items:center;
  justify-content:center; font-weight:800; font-size:11px; color:#fff; letter-spacing:.3px; }
.gmmsg-team { flex:1; }
.gmmsg-team-name { font-weight:800; font-size:15px; }
.gmmsg-team-sub { font-size:11px; color:#8fb3c9; }
.gmmsg-title { font-weight:900; font-size:34px; letter-spacing:-1px; margin:14px 0 14px; }

.gmmsg-mood { display:flex; align-items:center; gap:12px; background:#181818;
  border:1px solid #232323; border-left-width:4px; border-radius:12px; padding:12px 14px; margin-bottom:14px; }
.gmmsg-mood-emoji { font-size:26px; line-height:1; }
.gmmsg-mood-lab { font-size:8.5px; letter-spacing:.5px; color:#7f8b93; font-weight:700; text-transform:uppercase; }
.gmmsg-mood-val { font-weight:900; font-size:20px; letter-spacing:-.5px; }

.gmmsg-letter { position:relative; background:#181818; border:1px solid #232323; border-radius:13px;
  padding:18px 16px 14px; font-size:14px; line-height:1.55; color:#dfe6ec; }
.gmmsg-letter.fired { border-color:rgba(226,80,78,.4); background:rgba(226,80,78,.06); }
.gmmsg-letter p { margin:0 0 10px; }
.gmmsg-letter p:last-child { margin-bottom:0; }
.gmmsg-letter a { color:#18E0A8; }
.gmmsg-quote { position:absolute; top:2px; left:12px; font-size:44px; line-height:1; color:#2a3a48;
  font-family:Georgia, serif; }
.gmmsg-sign { margin-top:12px; font-weight:800; font-size:13px; color:#8fb3c9; }

.gmmsg-trend { background:#181818; border:1px solid #232323; border-radius:13px; padding:14px 15px;
  margin-top:14px; }
.gmmsg-trend-head { font-weight:800; font-size:14px; margin-bottom:12px; }
.gmmsg-trend-rows { display:flex; flex-direction:column; gap:9px; }
.gmmsg-trend-row { display:flex; align-items:center; gap:10px; }
.gmmsg-trend-yr { font-size:11px; font-weight:700; color:#8fb3c9; width:38px; flex:0 0 auto; }
.gmmsg-trend-bar { flex:1; height:8px; background:#232323; border-radius:6px; overflow:hidden; }
.gmmsg-trend-fill { height:100%; border-radius:6px; }
.gmmsg-trend-lab { font-size:10.5px; font-weight:800; width:64px; flex:0 0 auto; text-align:right; }
.gmmsg-trend-note { font-size:11px; color:#7f8b93; line-height:1.45; margin-top:12px; }

.gmmsg-empty { color:#8a8a8a; text-align:center; padding:40px; }

/* Fired state */
.gmmsg-fired { text-align:center; }
.gmmsg-firedbadge { font-size:52px; margin:14px 0 6px; }
.gmmsg-firedtitle { font-weight:900; font-size:32px; letter-spacing:-1px; margin:6px 0 6px;
  color:#E2504E; }
.gmmsg-firedsub { font-size:12px; color:#8fb3c9; margin-bottom:20px; }
.gmmsg-fired .gmmsg-letter { text-align:left; }
.gmmsg-firednote { font-size:13px; color:#9fb0bc; line-height:1.5; margin:18px 4px 22px; }
.gmmsg-cta { width:100%; border:none; border-radius:26px; background:#18E0A8; color:#0a0a0a;
  font-family:inherit; font-weight:800; font-size:16px; padding:15px; cursor:pointer;
  box-shadow:0 10px 26px rgba(24,224,168,.3); }
`;

export default GmMessage;
